import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFormAutosave } from '@/hooks/useFormAutosave';
import { reportFailedSubmission } from '@/hooks/useFormFailureBeacon';
import { RestoreDraftBanner } from '@/components/shared/RestoreDraftBanner';

const REGIONS = ['North America', 'Canada', 'Mexico'];

const TIME_ZONES = [
  { value: 'US/Eastern', label: 'Eastern Time (ET)' },
  { value: 'US/Central', label: 'Central Time (CT)' },
  { value: 'US/Mountain', label: 'Mountain Time (MT)' },
  { value: 'US/Pacific', label: 'Pacific Time (PT)' },
  { value: 'US/Alaska', label: 'Alaska Time (AKT)' },
  { value: 'US/Hawaii', label: 'Hawaii Time (HT)' },
  { value: 'Canada/Atlantic', label: 'Atlantic Time (AT)' },
  { value: 'Canada/Newfoundland', label: 'Newfoundland Time (NT)' },
  { value: 'America/Mexico_City', label: 'Mexico Central Time' },
  { value: 'America/Cancun', label: 'Mexico Eastern Time' },
  { value: 'America/Tijuana', label: 'Mexico Pacific Time' },
];

const CALLBACK_OPTIONS = [
  'As soon as possible',
  'Within 24 hours',
  'Within 2–3 business days',
  'Next week',
  'Flexible',
];

const CONTACT_METHODS = ['Phone', 'Email', 'Video call'];

const initialForm = {
  fullName: '',
  company: '',
  email: '',
  phone: '',
  region: '',
  timeZone: '',
  callbackTime: '',
  contactMethod: '',
  message: '',
};

const ScheduleCallModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    hasDraft, draftAge, restoreDraft, clearDraft, dismissDraftBanner,
  } = useFormAutosave('schedule-call', form, setForm, { enabled: !isSuccess });

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.email.trim()) e.email = 'Work email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.region) e.region = 'Region is required';
    if (!form.timeZone) e.timeZone = 'Time zone is required';
    if (!form.callbackTime) e.callbackTime = 'Preferred time frame is required';
    if (!form.message.trim()) e.message = 'Message is required';
    else if (form.message.length > 500) e.message = 'Message must be under 500 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/schedule-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submit failed');
      clearDraft();
      setIsSuccess(true);
    } catch (err) {
      console.error('Schedule call error:', err);
      reportFailedSubmission({
        formType: 'schedule_call',
        draft: form,
        errorReason: err?.message || 'fetch_failed',
      });
      setErrors(prev => ({ ...prev, submit: "We couldn't schedule your call. Please try again, or email sales@porticosystems.com." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setForm(initialForm);
      setErrors({});
      setIsSuccess(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="schedule-call-modal">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-stone-100">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-stone-100 transition-colors"
              aria-label="Close"
              data-testid="schedule-call-close-btn"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-stone-900">Schedule a Call</h2>
            </div>
            <p className="text-sm text-stone-500 leading-relaxed">
              Share a few details about yourself and what you'd like to discuss, and we'll follow up to find a time that fits your schedule.
            </p>
          </div>

          {isSuccess ? (
            <div className="p-8 text-center" data-testid="schedule-call-success">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2">We'll Be in Touch</h3>
              <p className="text-sm text-stone-500 mb-6 max-w-sm mx-auto">
                Thank you for your interest. A member of our team will reach out within your preferred time frame to schedule your call.
              </p>
              <Button onClick={handleClose} data-testid="schedule-call-done-btn">Done</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="schedule-call-form">
              <RestoreDraftBanner
                show={hasDraft}
                draftAge={draftAge}
                onRestore={restoreDraft}
                onDismiss={dismissDraftBanner}
                testId="schedulecall-restore-draft"
              />
              {/* Row 1: Name + Company */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name" required error={errors.fullName}>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={e => update('fullName', e.target.value)}
                    className={inputCls(errors.fullName)}
                    placeholder="Jane Doe"
                    data-testid="schedule-call-name"
                  />
                </Field>
                <Field label="Company Name">
                  <input
                    type="text"
                    value={form.company}
                    onChange={e => update('company', e.target.value)}
                    className={inputCls()}
                    placeholder="Acme Corp"
                    data-testid="schedule-call-company"
                  />
                </Field>
              </div>

              {/* Row 2: Email + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Work Email" required error={errors.email}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => update('email', e.target.value)}
                    className={inputCls(errors.email)}
                    placeholder="jane@company.com"
                    data-testid="schedule-call-email"
                  />
                </Field>
                <Field label="Phone Number">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => update('phone', e.target.value)}
                    className={inputCls()}
                    placeholder="(555) 123-4567"
                    data-testid="schedule-call-phone"
                  />
                </Field>
              </div>

              {/* Row 3: Region + Time Zone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Region" required error={errors.region}>
                  <select
                    value={form.region}
                    onChange={e => update('region', e.target.value)}
                    className={selectCls(errors.region)}
                    data-testid="schedule-call-region"
                  >
                    <option value="">Select region</option>
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Time Zone" required error={errors.timeZone}>
                  <select
                    value={form.timeZone}
                    onChange={e => update('timeZone', e.target.value)}
                    className={selectCls(errors.timeZone)}
                    data-testid="schedule-call-timezone"
                  >
                    <option value="">Select time zone</option>
                    {TIME_ZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </select>
                </Field>
              </div>

              {/* Row 4: Callback + Contact Method */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Preferred Callback Time Frame" required error={errors.callbackTime}>
                  <select
                    value={form.callbackTime}
                    onChange={e => update('callbackTime', e.target.value)}
                    className={selectCls(errors.callbackTime)}
                    data-testid="schedule-call-callback"
                  >
                    <option value="">Select time frame</option>
                    {CALLBACK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Preferred Contact Method">
                  <select
                    value={form.contactMethod}
                    onChange={e => update('contactMethod', e.target.value)}
                    className={selectCls()}
                    data-testid="schedule-call-method"
                  >
                    <option value="">Select method</option>
                    {CONTACT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
              </div>

              {/* Message */}
              <Field label="What would you like to talk about?" required error={errors.message}>
                <textarea
                  value={form.message}
                  onChange={e => update('message', e.target.value)}
                  className={`${inputCls(errors.message)} min-h-[100px] resize-none`}
                  placeholder="We'd like to learn more about your services, pricing, and implementation timeline."
                  maxLength={500}
                  rows={4}
                  data-testid="schedule-call-message"
                />
                <p className="text-xs text-stone-400 mt-1 text-right">{form.message.length}/500</p>
              </Field>

              {/* Submit */}
              <div className="pt-2">
                {errors.submit && (
                  <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg mb-3" data-testid="schedule-call-submit-error">{errors.submit}</p>
                )}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full gap-2 font-semibold"
                  disabled={isSubmitting}
                  data-testid="schedule-call-submit"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Request a Call
                    </>
                  )}
                </Button>
              </div>

              {/* Privacy note */}
              <p className="text-xs text-stone-400 text-center leading-relaxed">
                By submitting this form, you agree to be contacted by our team regarding your inquiry.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

/* ── Helpers ── */
const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-stone-700 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const inputCls = (error) =>
  `w-full px-3.5 py-2.5 text-sm border rounded-lg transition-colors outline-none
   ${error
     ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-200'
     : 'border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20'
   } bg-white placeholder:text-stone-400`;

const selectCls = (error) =>
  `w-full px-3.5 py-2.5 text-sm border rounded-lg transition-colors outline-none appearance-none bg-white
   ${error
     ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-200'
     : 'border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20'
   } ${!error ? 'text-stone-900' : 'text-stone-900'}`;

export default ScheduleCallModal;
